import React, { useEffect, useRef, useState } from 'react';
import { InfoWindow } from '@vis.gl/react-google-maps';
import { useMap } from '@vis.gl/react-google-maps';
import LocationInfoWindow from './LocationInfoWindow';
import { FirestoreLocation, type PotentialLocation } from '@/types/firestore';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { fetchActiveLocationData, clearActiveLocation, selectActiveLocationDetailsError } from '@/store/slices/activeLocationSlice';
import { 
    selectActiveInfoWindowId, 
    selectPotentialLocations, 
    setActiveInfoWindowId,
    updatePotentialLocation,
    removePotentialLocation,
} from '@/store/slices/mapUiSlice';
import { selectAllLocations } from '@/store/slices/locationsSlice';
import { useTheme } from 'next-themes';
import { toast } from 'sonner';

/**
 * This component manages all info windows for the map in a centralized way.
 * Using the InfoWindow component from @vis.gl/react-google-maps.
 */
const MapInfoWindowController: React.FC = () => {
  const previousActiveIdRef = useRef<string | null>(null);
  const { theme } = useTheme();
  
  // Get the actual card background color from the root element
  const getCardColor = () => {
    // Get theme-specific card background color
    if (typeof document !== 'undefined') {
      return getComputedStyle(document.documentElement).getPropertyValue('--card').trim() || '#ffffff';
    }
    return '#ffffff'; // Default white fallback
  };
    
  const map = useMap();
  const dispatch = useAppDispatch();

  const activeInfoWindowId = useAppSelector(selectActiveInfoWindowId);
  const allLocations: FirestoreLocation[] = useAppSelector(selectAllLocations);
  const potentialLocations = useAppSelector(selectPotentialLocations);
  const locationError = useAppSelector(selectActiveLocationDetailsError);
  
  // Get card background color reactively
  const [, setCardBgColor] = useState(getCardColor());
  
  // Update card color when theme changes
  useEffect(() => {
    setCardBgColor(getCardColor());
  }, [theme]);

  // Handle location error (including deletion)
  useEffect(() => {
    if (locationError && activeInfoWindowId && !activeInfoWindowId.startsWith('potential-')) {
      // Check if the error is "Location not found" which indicates deletion
      if (locationError === 'Location not found') {
        toast.error(`Location has been deleted from the database.`);
        dispatch(setActiveInfoWindowId(null));
      }
    }
  }, [locationError, activeInfoWindowId, dispatch]);

  const activeLocation: PotentialLocation | null = React.useMemo(() => {
    if (!activeInfoWindowId) return null;
    
    const potential = potentialLocations.find((p: PotentialLocation) => p.id === activeInfoWindowId);
    if (potential) {
      return potential;
    }

    // Check for the location using the 'id' field from FirestoreLocation
    const standard = allLocations?.find((l: FirestoreLocation) => l.id === activeInfoWindowId);
    
    if (standard) {
        // Convert FirestoreLocation to the structure needed by LocationInfoWindow
        
        // Coordinates are now plain objects from the store
        let coords = { latitude: 0, longitude: 0 }; // Default coords
        if (standard.coordinates && 
            typeof standard.coordinates.latitude === 'number' &&
            typeof standard.coordinates.longitude === 'number') {
            coords = { 
                latitude: standard.coordinates.latitude, 
                longitude: standard.coordinates.longitude 
            };
        } 

        // Timestamps are now numbers (milliseconds) from the store
        let lastSightingDateMillis: number | undefined = undefined;
        if (typeof standard.lastSightingDate === 'number') {
            lastSightingDateMillis = standard.lastSightingDate;
        }
        // If lastSightingDate was stored as an ISO string and needs conversion back to millis,
        // that would need different handling, but our serializer uses toMillis().

        return { 
            id: standard.id, // Use the Firestore ID
            name: standard.name || "Unknown Place",
            address: standard.address || "Address unavailable",
            coordinates: coords, 
            locationType: standard.locationType || 'other',
            googlePlaceId: standard.googlePlaceId || undefined, 
            averageRating: standard.averageRating,
            ratingCount: standard.ratingCount,
            drinkCount: standard.drinkCount,
            lastSightingDate: lastSightingDateMillis,
        } as PotentialLocation; // Cast might still be okay if PotentialLocation expects this structure
    }
    
    return null;
  }, [activeInfoWindowId, allLocations, potentialLocations]);

  useEffect(() => {
    if (activeInfoWindowId) {
        if (!activeInfoWindowId.startsWith('potential-')) {
           dispatch(fetchActiveLocationData(activeInfoWindowId));
        } else {
           dispatch(clearActiveLocation()); 
        }
    } else {
      dispatch(clearActiveLocation());
    }
    previousActiveIdRef.current = activeInfoWindowId;
  }, [activeInfoWindowId, dispatch]);

  const handleClose = () => {
    dispatch(setActiveInfoWindowId(null));
  };

  const handleUpdatePotential = (updates: Partial<PotentialLocation>) => {
    if (activeLocation?.id?.startsWith('potential-')) {
        const updatedLocation: PotentialLocation = {
            ...activeLocation,
            ...updates,
            coordinates: updates.coordinates || activeLocation.coordinates,
        };
        dispatch(updatePotentialLocation(updatedLocation));
    } else {
        console.warn("[MapInfoWindowController] Attempted to update a non-potential location via handleUpdatePotential");
    }
  };

  const handleRemoveCurrentPotential = () => {
      if (activeLocation?.id?.startsWith('potential-')) {
          dispatch(removePotentialLocation(activeLocation.id));
          handleClose();
      } else {
           console.warn("[MapInfoWindowController] Attempted to remove a non-potential location via handleRemoveCurrentPotential");
      }
  };

  if (!map || !activeLocation) {
    return null;
  }
  
  const position = activeLocation.coordinates && 
                   typeof activeLocation.coordinates.latitude === 'number' && 
                   typeof activeLocation.coordinates.longitude === 'number'
      ? { lat: activeLocation.coordinates.latitude, lng: activeLocation.coordinates.longitude } 
      : null;

  if (!position) {
      console.warn("Active location has no valid coordinates object, cannot display InfoWindow.", activeLocation);
      return null;
  }

  return (
    <InfoWindow
      position={position}
      onCloseClick={handleClose}
      pixelOffset={[0, -30]}
      disableAutoPan={false}
      headerDisabled={true}
    >
        <LocationInfoWindow 
            key={activeLocation.id} 
            location={activeLocation}
            onClose={handleClose} 
            onUpdate={handleUpdatePotential} 
            onRemove={handleRemoveCurrentPotential} 
        />
    </InfoWindow>
  );
};

export default React.memo(MapInfoWindowController); 